import * as React from 'react';
import { useLocalStorageState } from '../../../../hooks/useLocalStorage';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { Channel, TicketPriority } from '../../../../models/Support/SupportEnums';
import { useAdminAccountQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useSupportCategoriesQuery } from '../../../../queries/Support/useSupportCategories';
import type { ISupportMailbox } from '../../../../queries/Support/useSupportMailboxQuery';
import { LAST_MAILBOX_STORAGE_KEY } from '../supportStorageKeys';
import { useAgentMailboxes } from '../useAgentMailboxes';
import {
	getNewTicketDraftStorageKey,
	type NewTicketDraftAccount,
	type NewTicketDraftContact,
	type NewTicketMode,
	readStoredNewTicketDraft,
	type StoredNewTicketDraft,
	useNewTicketDraft,
} from './newTicketDraft';

export interface NewTicketDraftSource {
	initialDraft: StoredNewTicketDraft | null;
	storageKey: string;
}

/** Read once at mount, by the caller, so a mode seeding its own state from it can do so synchronously. */
export function useNewTicketDraftSource(mode: NewTicketMode, accountId?: string): NewTicketDraftSource {
	const userSession = useUserSession();
	const storageKey = getNewTicketDraftStorageKey(userSession.user?.id ?? '', accountId, mode);
	const [initialDraft] = React.useState(() => readStoredNewTicketDraft(storageKey));
	return { initialDraft, storageKey };
}

interface UseNewTicketFormStateOptions {
	defaultAccount?: NewTicketDraftAccount;
	defaultMailboxId?: string;
	draftSource: NewTicketDraftSource;
	/** Internal only; an outbound ticket's first email is written in the composer. */
	descriptionHtml?: string;
	channel?: Channel;
}

export interface NewTicketFormState {
	account: NewTicketDraftAccount | null;
	categories: { id: string; name: string }[];
	changeAccount: (account: NewTicketDraftAccount | null) => void;
	changeContact: (contact: NewTicketDraftContact | null, contactAccount?: NewTicketDraftAccount) => void;
	clearDraft: () => void;
	csmName?: string;
	/** Settled, succeeded, and the user is an agent nowhere — distinct from loading and from failure. */
	hasNoAgentMailbox: boolean;
	mailboxes: ISupportMailbox[];
	mailboxesFailed: boolean;
	mailboxesLoading: boolean;
	mailboxId: string;
	manualContact: boolean;
	contactError: string;
	contact: NewTicketDraftContact | null;
	priority: TicketPriority;
	setCategoryId: (categoryId: string) => void;
	setContactError: (message: string) => void;
	setPriority: (priority: TicketPriority) => void;
	setSelectedMailboxId: (mailboxId: string) => void;
	setSubject: (subject: string) => void;
	subject: string;
	toggleManualContact: (manual: boolean) => void;
	validCategoryId: string;
}

/**
 * State both new-ticket modals share. They stay separate components because internal mounts a
 * description editor and two uploader hooks that outbound must not, and hooks cannot be conditional.
 */
export function useNewTicketFormState({
	defaultAccount,
	defaultMailboxId,
	draftSource,
	descriptionHtml = '',
	channel = Channel.Email,
}: UseNewTicketFormStateOptions): NewTicketFormState {
	const { initialDraft, storageKey } = draftSource;

	const [subject, setSubject] = React.useState(initialDraft?.subject ?? '');
	const [priority, setPriority] = React.useState<TicketPriority>(initialDraft?.priority ?? TicketPriority.Normal);
	const [categoryId, setCategoryId] = React.useState(initialDraft?.categoryId ?? '');
	const [account, setAccount] = React.useState<NewTicketDraftAccount | null>(
		initialDraft?.account ?? defaultAccount ?? null
	);
	const [contact, setContact] = React.useState<NewTicketDraftContact | null>(initialDraft?.contact ?? null);
	const [manualContact, setManualContact] = React.useState(initialDraft?.manualContact ?? false);
	const [selectedMailboxId, setSelectedMailboxId] = React.useState(initialDraft?.mailboxId ?? defaultMailboxId ?? '');
	const [contactError, setContactError] = React.useState('');

	const { mailboxes, isLoading: mailboxesLoading, isError: mailboxesFailed } = useAgentMailboxes();
	const [storedMailboxId] = useLocalStorageState<string>(LAST_MAILBOX_STORAGE_KEY, '');
	const isKnownMailbox = (id: string) => !!id && mailboxes.some(m => m.id === id);
	const fallbackMailboxId = mailboxes.find(m => m.isActive)?.id ?? mailboxes[0]?.id ?? '';
	const mailboxId = isKnownMailbox(selectedMailboxId)
		? selectedMailboxId
		: isKnownMailbox(storedMailboxId)
			? storedMailboxId
			: fallbackMailboxId;

	const categoriesQuery = useSupportCategoriesQuery(mailboxId);
	const categories = categoriesQuery.data?.values ?? [];
	const validCategoryId = categories.some(category => category.id === categoryId) ? categoryId : '';

	// The CSM lives on the account (the ticket inherits it server-side); shown so the submitter
	// knows who will own the follow-up. Errors stay silent — the row just shows the fallback.
	const accountQuery = useAdminAccountQuery({ accountId: account?.id ?? '', enabled: !!account?.id });
	const csmName = accountQuery.data?.additionalInfo?.customerSuccessName;

	const { clearDraft } = useNewTicketDraft({
		storageKey,
		subject,
		descriptionHtml,
		priority,
		channel,
		categoryId: validCategoryId,
		mailboxId,
		account,
		contact,
		manualContact,
	});

	const changeContact = (nextContact: NewTicketDraftContact | null, contactAccount?: NewTicketDraftAccount) => {
		setContact(nextContact);
		setContactError('');
		if (contactAccount) setAccount(contactAccount);
	};

	// A picked contact belongs to the account it came from; a manually typed address belongs to no
	// account, so it survives the switch rather than being erased mid-edit.
	const changeAccount = (nextAccount: NewTicketDraftAccount | null) => {
		setAccount(nextAccount);
		if (!manualContact && nextAccount?.id !== account?.id) setContact(null);
	};

	const toggleManualContact = (manual: boolean) => {
		setManualContact(manual);
		setContact(null);
		setContactError('');
	};

	return {
		account,
		categories,
		changeAccount,
		changeContact,
		clearDraft,
		contact,
		csmName,
		contactError,
		hasNoAgentMailbox: !mailboxesLoading && !mailboxesFailed && mailboxes.length === 0,
		mailboxes,
		mailboxesFailed,
		mailboxesLoading,
		mailboxId,
		manualContact,
		priority,
		setCategoryId,
		setContactError,
		setPriority,
		setSelectedMailboxId,
		setSubject,
		subject,
		toggleManualContact,
		validCategoryId,
	};
}
